import { type_GM_STATE } from '../../helpers'
import { WishlistListItemDraft } from './list-item'
import { attachRequiredStorefrontInformationToOmneoItem, getStorefrontProductsBySkus, itemShouldBeHidden } from './utils'
import { OmneoList, OmneoListDefinitionHandle, OmneoListItem, OmneoListItemMeta } from '../types'

declare var GM_STATE: type_GM_STATE

type SwymListItem = {
  id?: string | number
  di?: string | number
  lid: string
  empi?: string | number
  epi?: string | number
  du?: string
  iu?: string
  sku?: string
  dt?: string
  bt?: string
  pr?: number
  stk?: number
  prodstatus?: string
  cts?: number
  uts?: number
  qty?: number
  cprops?: Record<string, unknown>
}

type SwymList = {
  lid: string
  lname: string
  lty?: string | null
  lnote?: string
  lprops?: Record<string, unknown>
  anonRead?: boolean
  cnt?: number
  cts?: number
  uts?: number
  lhash?: string
  listcontents?: SwymListItem[]
  userinfo?: {
    em?: string
  }
}

type SwymRuntime = {
  fetchLists: (config: { callbackFn: (lists: SwymList[]) => void; errorFn?: (error: unknown) => void; lty?: string }) => void
  createList: (config: { lname: string }, onSuccess: (list: SwymList) => void, onError: (error: unknown) => void) => void
  updateList: (config: { lid: string; lprops?: Record<string, unknown>; lnote?: string; lname?: string }, onSuccess: (list: SwymList) => void, onError: (error: unknown) => void) => void
  addToList: (lid: string, product: Record<string, unknown>, onSuccess: (item: SwymListItem) => void, onError: (error: unknown) => void) => void
  deleteFromList: (lid: string, product: Record<string, unknown>, onSuccess: (result: unknown) => void, onError: (error: unknown) => void) => void
  deleteList?: (lid: string, onSuccess: (result: unknown) => void, onError: (error: unknown) => void) => void
  markListPublic: (lid: string, onSuccess: (result: unknown) => void, onError: (error: unknown) => void) => void
  generateSharedListURL: (lid: string, onSuccess: (url: string, lid?: string) => void, onError: (error: unknown) => void) => void
  evtLayer?: {
    addEventListener: (eventName: string, handler: EventListenerOrEventListenerObject) => void
    removeEventListener: (eventName: string, handler: EventListenerOrEventListenerObject) => void
  }
  platform?: {
    isLoggedIn?: () => boolean
  }
  sharedWlApi?: {
    internalFn: (
      config: { params: { hkey: string } },
      onSuccess: (result: SwymSharedListResponse) => void,
      onError: (error: unknown) => void
    ) => void
  }
}

type SwymWindow = Window & {
  _swat?: SwymRuntime
  SwymCallbacks?: Array<(swat: SwymRuntime) => void>
}

type SwymDeletePayload =
  | { id: string }
  | { epi?: string | number; empi?: string | number; du?: string }

type NormalizedSwymIdentity = {
  rowId?: string
  lid?: string
  epi?: string | number
  empi?: string | number
  du?: string
}

type CreateListInput = {
  name: string
  isShared?: boolean
  initialItem?: WishlistListItemDraft | null
}

type CreateListError = {
  errorMessage: string
}

type CreateListResult = OmneoList | CreateListError | null

type UpdateListInput = {
  name: string
  isShared: boolean
}

const SWYM_SHARE_URL_PROP = '__sasWishlistShareUrl'
const SWYM_READY_TIMEOUT_MS = 10000
const SWYM_LIST_EVENT_NAMES = ['sw:addedtowishlist', 'sw:removedfromwishlist', 'sw:customerInfoRefreshed'] as const

type CachedListsPayload = {
  ownerKey: string
  lists: OmneoList[]
}

type SwymSharedListResponse = {
  querystr?: Record<string, unknown>
  queryres?: SwymListItem[]
}

const defaultListDefinition = {
  id: 1,
  name: 'Wishlist',
  handle: 'wishlist' as OmneoListDefinitionHandle,
  type: 'store' as const,
  icon: null,
  cover: null,
  short_description: '',
  description: null,
  allow_share: true,
  allow_quantity: false,
  allow_reserve: false,
  allow_custom_product: false,
  meta: null,
  is_published: true,
  is_active: true,
  tags: [] as string[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isCreateListError(value: unknown): value is CreateListError {
  return isRecord(value) && typeof value.errorMessage === 'string'
}

function toNumber(value: unknown, fallback = 0) {
  const parsedValue = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : fallback
}

function toIsoDate(value?: string | number | null) {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const timestamp = typeof value === 'string' && value.includes('T') ? Date.parse(value) : Number(value)
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return ''
  }

  return new Date(timestamp).toISOString()
}

function slugifyListName(name: string) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

function hasAuthenticatedAccountRouteHint() {
  if (typeof document === 'undefined') {
    return false
  }

  if (document.querySelector('a[href*="/account/logout"]')) {
    return true
  }

  const body = document.body

  return Boolean(body?.classList.contains('template-account') && !body.classList.contains('template-login'))
}

function hasAuthenticatedCustomerState() {
  const firstName = GM_STATE.customer.firstName?.trim()

  return Boolean(GM_STATE.customer.logged || GM_STATE.customer.id || firstName || hasAuthenticatedAccountRouteHint())
}

function normalizeEmail(value: unknown) {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

function getCurrentCustomerEmail() {
  return normalizeEmail(GM_STATE.customer.email)
}

function getCurrentCustomerId() {
  return GM_STATE.customer.id !== null && GM_STATE.customer.id !== undefined ? String(GM_STATE.customer.id) : ''
}

function getWishlistOwnerKey() {
  const customerId = getCurrentCustomerId()
  if (customerId) {
    return `customer:${customerId}`
  }

  const customerEmail = getCurrentCustomerEmail()
  if (customerEmail) {
    return `email:${customerEmail}`
  }

  return hasAuthenticatedCustomerState() ? 'authenticated:unknown' : 'guest'
}

function getWishlistCacheStorageKey() {
  return 'wishlistsGlobal'
}

function getCachedLists() {
  try {
    const rawValue = sessionStorage.getItem(getWishlistCacheStorageKey())
    if (!rawValue) {
      return []
    }

    const parsedValue = JSON.parse(rawValue) as CachedListsPayload | OmneoList[]
    if (Array.isArray(parsedValue)) {
      return parsedValue
    }

    if (parsedValue.ownerKey !== getWishlistOwnerKey() || !Array.isArray(parsedValue.lists)) {
      return []
    }

    return parsedValue.lists
  } catch {
    return []
  }
}

function setCachedLists(lists: OmneoList[]) {
  const payload: CachedListsPayload = {
    ownerKey: getWishlistOwnerKey(),
    lists
  }

  try {
    sessionStorage.setItem(getWishlistCacheStorageKey(), JSON.stringify(payload))
  } catch {
    // Ignore cache write failures; wishlist should still function without caching.
  }
}

function clearCachedLists() {
  sessionStorage.removeItem(getWishlistCacheStorageKey())
}

function extractQuotedMessage(value: string) {
  const matches = value.match(/"([^"]{3,})"/g)
  if (!matches?.length) {
    return null
  }

  const normalizedMatches = matches
    .map((match) => match.replace(/^"|"$/g, '').trim())
    .filter((match) => match && !['not', 'lname'].includes(match.toLowerCase()))

  return normalizedMatches[0] || null
}

function extractProviderErrorMessage(error: unknown): string | null {
  if (typeof error === 'string') {
    return extractQuotedMessage(error) || error
  }

  if (error instanceof Error) {
    return extractQuotedMessage(error.message) || error.message
  }

  if (!isRecord(error)) {
    return null
  }

  const nestedErrors = error.errors
  if (isRecord(nestedErrors)) {
    for (const value of Object.values(nestedErrors)) {
      if (typeof value === 'string') {
        return extractQuotedMessage(value) || value
      }

      if (Array.isArray(value)) {
        const firstString = value.find((item): item is string => typeof item === 'string')
        if (firstString) {
          return extractQuotedMessage(firstString) || firstString
        }
      }
    }
  }

  if (typeof error.message === 'string' && error.message.trim()) {
    return extractQuotedMessage(error.message) || error.message
  }

  return null
}

function getSwymShareUrl(list: SwymList) {
  const lprops = isRecord(list.lprops) ? list.lprops : {}
  return typeof lprops[SWYM_SHARE_URL_PROP] === 'string' ? lprops[SWYM_SHARE_URL_PROP] : ''
}

function getSwymCallbacks() {
  const swymWindow = window as SwymWindow
  swymWindow.SwymCallbacks = swymWindow.SwymCallbacks || []
  return swymWindow.SwymCallbacks as Array<(swat: SwymRuntime) => void>
}

function setWishlistProviderReady() {
  if (window.wishlistProvider) {
    window.wishlistProvider.ready = true
  }
}

function getSwymRuntime(): Promise<SwymRuntime> {
  const swymWindow = window as SwymWindow
  if (swymWindow._swat) {
    setWishlistProviderReady()
    return Promise.resolve(swymWindow._swat)
  }

  return new Promise<SwymRuntime>((resolve, reject) => {
    let isSettled = false
    const timeoutId = window.setTimeout(() => {
      if (isSettled) return
      isSettled = true
      reject(new Error('Swym SDK not ready'))
    }, SWYM_READY_TIMEOUT_MS)

    getSwymCallbacks().push((swat) => {
      if (isSettled) return
      isSettled = true
      window.clearTimeout(timeoutId)
      setWishlistProviderReady()
      resolve(swat)
    })
  })
}

function whenSwymReady(callback: (swat: SwymRuntime) => void) {
  const swymWindow = window as SwymWindow
  if (swymWindow._swat) {
    setWishlistProviderReady()
    callback(swymWindow._swat)
    return () => {}
  }

  let isDisposed = false
  getSwymCallbacks().push((swat) => {
    if (!isDisposed) {
      setWishlistProviderReady()
      callback(swat)
    }
  })

  return () => {
    isDisposed = true
  }
}

function swymPromise<T>(invoke: (swat: SwymRuntime, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void) {
  return getSwymRuntime().then(
    (swat) =>
      new Promise<T>((resolve, reject) => {
        invoke(swat, resolve, reject)
      })
  )
}

function buildSwymProductInput(initialItem: WishlistListItemDraft) {
  const variantId = initialItem?.meta?.variantId
  const productId = initialItem?.meta?.productId
  const handle = initialItem?.meta?.handle
  const productUrl = handle ? `${location.origin}/products/${handle}?variant=${variantId}` : location.href

  return {
    epi: variantId,
    empi: productId,
    du: productUrl,
    cprops: initialItem.meta,
    source: 'pdp'
  }
}

function normalizeSharedSwymList(handle: string, sharedList: SwymSharedListResponse) {
  const querystr = isRecord(sharedList.querystr) ? sharedList.querystr : {}
  const rawItems = Array.isArray(sharedList.queryres) ? sharedList.queryres : []
  const listId = querystr.lid !== undefined && querystr.lid !== null ? String(querystr.lid) : handle
  const shareUrl = `${location.origin}/pages/${GM_STATE.wishlist.sharedWishlist.page}?handle=${handle}`

  return {
    lid: listId,
    lname: typeof querystr.lname === 'string' && querystr.lname.trim() ? querystr.lname : 'Wishlist',
    lty: 'wl',
    lnote: typeof querystr.lnote === 'string' ? querystr.lnote : '',
    lprops: {
      [SWYM_SHARE_URL_PROP]: shareUrl
    },
    anonRead: true,
    cnt: rawItems.length,
    cts: typeof querystr.cts === 'number' || typeof querystr.cts === 'string' ? querystr.cts : Date.now(),
    uts: typeof querystr.uts === 'number' || typeof querystr.uts === 'string' ? querystr.uts : querystr.cts,
    lhash: handle,
    listcontents: rawItems.map((item) => ({
      ...item,
      lid: item.lid || listId
    })),
    userinfo: {
      em: typeof querystr.regemail === 'string' ? querystr.regemail : ''
    }
  } as SwymList
}

function getNormalizedSwymIdentity(item: OmneoListItem) {
  return ((item as OmneoListItem & { swym?: NormalizedSwymIdentity }).swym || {}) as NormalizedSwymIdentity
}

function buildSwymDeletePayloads(currentList: OmneoList, item: OmneoListItem): SwymDeletePayload[] {
  const swymIdentity = getNormalizedSwymIdentity(item)
  const itemId = item.id !== null && item.id !== undefined ? String(item.id) : ''
  const rawRowId = swymIdentity.rowId || itemId
  const variantId = swymIdentity.epi ?? item.meta?.variantId
  const productId = swymIdentity.empi ?? item.meta?.productId
  const handle = item.meta?.handle
  const productUrl = swymIdentity.du || item.product?.external_id || (handle ? `${location.origin}/products/${handle}?variant=${variantId}` : '')
  const deletePayloads: SwymDeletePayload[] = []

  if (rawRowId) {
    deletePayloads.push({ id: rawRowId })
  }

  const fallbackPayload = {
    ...(variantId ? { epi: variantId } : {}),
    ...(productId ? { empi: productId } : {}),
    ...(productUrl ? { du: productUrl } : {})
  }

  if (Object.keys(fallbackPayload).length > 0) {
    deletePayloads.push(fallbackPayload)
  }

  if (!deletePayloads.length) {
    console.error('Could not build Swym delete payload for wishlist item', {
      currentListId: currentList.id,
      itemId: item.id,
      handle: item.meta?.handle,
      variantId: item.meta?.variantId,
      productId: item.meta?.productId
    })
  }

  return deletePayloads.filter((payload, index, payloads) => {
    const serializedPayload = JSON.stringify(payload)
    return payloads.findIndex((candidate) => JSON.stringify(candidate) === serializedPayload) === index
  })
}

function normalizeSwymItem(item: SwymListItem): OmneoListItem {
  const itemProps = isRecord(item.cprops) ? item.cprops : {}
  const variantId = toNumber(item.epi || itemProps.variantId)
  const productId = toNumber(item.empi || itemProps.productId)
  const handle = typeof itemProps.handle === 'string' ? itemProps.handle : ''
  const variantAvailable = typeof itemProps.variantAvailable === 'boolean' ? itemProps.variantAvailable : item.stk !== 0
  const productStatus = typeof item.prodstatus === 'string' && item.prodstatus.trim() ? item.prodstatus.toLowerCase() : 'active'
  const imageSrc = typeof itemProps.image === 'string'
    ? itemProps.image
    : isRecord(itemProps.image) && typeof itemProps.image.src === 'string'
      ? itemProps.image.src
      : item.iu || ''
  const imageAlt = isRecord(itemProps.image) && typeof itemProps.image.alt === 'string'
    ? itemProps.image.alt
    : item.dt || item.bt || ''

  const meta = {
    ...itemProps,
    productId,
    variantId,
    handle,
    variantSku: typeof itemProps.variantSku === 'string' ? itemProps.variantSku : item.sku || '',
    variantPrice: typeof itemProps.variantPrice === 'number' ? itemProps.variantPrice : toNumber(item.pr),
    variantOptions: Array.isArray(itemProps.variantOptions) ? itemProps.variantOptions : [],
    variantAvailable,
    templateSuffix:
      typeof itemProps.templateSuffix === 'string' && itemProps.templateSuffix.trim()
        ? itemProps.templateSuffix
        : 'default',
    productBiline: typeof itemProps.productBiline === 'string' ? itemProps.productBiline : '',
    image: {
      src: imageSrc,
      alt: imageAlt
    },
    properties: itemProps.properties,
    productPersonalisedPrice:
      typeof itemProps.productPersonalisedPrice === 'string'
        ? itemProps.productPersonalisedPrice
        : itemProps.productPersonalisedPrice !== undefined
          ? String(itemProps.productPersonalisedPrice)
          : undefined
  } as OmneoListItemMeta

  const normalizedItem = {
    id: item.id || `${item.lid}:${variantId}`,
    custom_product: null,
    meta,
    position: null,
    product: {
      id: productId,
      title: item.dt || item.bt || '',
      handle,
      external_id: item.du || '',
      status: productStatus,
      brand: '',
      description: item.dt || '',
      currency: '',
      position: 0,
      systems: [],
      channels: [],
      images: item.iu ? [{ url: item.iu, sort_order: 0 }] : [],
      tags: [],
      options: [],
      variants: []
    },
    product_category: {
      id: 0,
      name: 'wishlist'
    },
    product_list_id: 0,
    product_variant: {
      id: variantId,
      product_id: productId,
      title: item.bt || item.dt || '',
      sku: typeof itemProps.variantSku === 'string' ? itemProps.variantSku : item.sku || '',
      description: '',
      available_quantity: variantAvailable ? 1 : 0,
      price: toNumber(item.pr),
      price_discounted: toNumber(item.pr),
      price_comparison: 0,
      price_cost: 0,
      position: 0,
      tags: [],
      options: []
    },
    quantity: item.qty || 1,
    reservations: [],
    status: null,
    created_at: toIsoDate(item.cts),
    updated_at: toIsoDate(item.uts)
  } as OmneoListItem

  ;(normalizedItem as OmneoListItem & { swym?: NormalizedSwymIdentity }).swym = {
    rowId: item.id !== null && item.id !== undefined ? String(item.id) : item.di !== null && item.di !== undefined ? String(item.di) : undefined,
    lid: item.lid,
    epi: item.epi,
    empi: item.empi,
    du: item.du
  }

  return normalizedItem
}

function normalizeSwymList(list: SwymList): OmneoList {
  const items = (list.listcontents || []).map(normalizeSwymItem)
  const shareUrl = getSwymShareUrl(list)
  const firstImage = items
    .map((item) => item.meta?.image)
    .find((image): image is OmneoListItemMeta['image'] & { src: string; alt: string } => isRecord(image) && typeof image.src === 'string' && typeof image.alt === 'string')

  return {
    id: list.lid,
    profile_id: list.userinfo?.em || '',
    name: list.lname,
    description: list.lnote || null,
    meta: {
      ...(firstImage ? { image: firstImage } : {}),
      ...(shareUrl ? { shareUrl } : {})
    },
    handle: list.lhash || slugifyListName(list.lname),
    sort_order: null,
    items,
    is_shared: Boolean(list.anonRead),
    shares: list.anonRead
      ? [{
          id: 0,
          handle: shareUrl,
          profile_id: list.userinfo?.em || '',
          product_list_id: 0,
          created_at: toIsoDate(list.cts),
          updated_at: toIsoDate(list.uts)
        }]
      : [],
    tags: [],
    custom_fields: [],
    definition: defaultListDefinition,
    custom_date: null,
    location: null,
    staff: null,
    created_at: toIsoDate(list.cts),
    updated_at: toIsoDate(list.uts),
    lastUpdate: toIsoDate(list.uts || list.cts)
  }
}

async function enrichSwymLists(lists: SwymList[]) {
  const normalizedLists = lists
    .filter((list) => !list.lty || list.lty === 'wl')
    .map(normalizeSwymList)

  const skusNeedingHandles = normalizedLists
    .flatMap((list) => list.items)
    .filter((item) => !itemShouldBeHidden(item))
    .map((item) => item.product_variant.sku)
    .filter(Boolean)

  const storefrontProducts = await getStorefrontProductsBySkus([...new Set(skusNeedingHandles)])

  for (const list of normalizedLists) {
    for (const item of list.items) {
      if (itemShouldBeHidden(item)) continue
      attachRequiredStorefrontInformationToOmneoItem(item, storefrontProducts, list.definition.type)
    }

    const lastUpdatedItem = list.items
      .map((item) => item.updated_at || item.created_at)
      .filter(Boolean)
      .sort((itemA, itemB) => (itemA > itemB ? -1 : 1))[0]

    list.lastUpdate = lastUpdatedItem || list.updated_at || list.created_at
  }

  normalizedLists.sort((listA, listB) => ((listA.lastUpdate || '') > (listB.lastUpdate || '') ? -1 : 1))
  return normalizedLists
}

async function fetchSwymLists() {
  const lists = await swymPromise<SwymList[]>((swat, resolve, reject) => {
    swat.fetchLists({
      callbackFn: resolve,
      errorFn: reject
    })
  }).catch((error) => {
    console.error('Could not fetch Swym lists', error)
    return []
  })

  return enrichSwymLists(lists)
}

async function getSwymSharedList(handle: string) {
  const sharedList = await swymPromise<SwymSharedListResponse>((swat, resolve, reject) => {
    if (!swat.sharedWlApi?.internalFn) {
      reject(new Error('Swym shared wishlist API is unavailable'))
      return
    }

    swat.sharedWlApi.internalFn(
      {
        params: {
          hkey: handle
        }
      },
      resolve,
      reject
    )
  }).catch((error) => {
    console.error('Could not fetch Swym shared list', handle, error)
    return null
  })

  if (!sharedList) {
    return null
  }

  const [list] = await enrichSwymLists([normalizeSharedSwymList(handle, sharedList)])
  return list || null
}

async function getSwymListById(listId: string | number) {
  const lists = await fetchSwymLists()
  return lists.find((list) => list.id.toString() === listId.toString()) || null
}

async function persistSwymShareUrl(currentList: OmneoList, shareUrl: string) {
  const existingMeta = isRecord(currentList.meta) ? currentList.meta : {}
  const existingLprops = isRecord(existingMeta.lprops) ? existingMeta.lprops : {}

  return swymPromise<SwymList>((swat, resolve, reject) => {
    swat.updateList(
      {
        lid: currentList.id.toString(),
        lprops: {
          ...existingLprops,
          [SWYM_SHARE_URL_PROP]: shareUrl
        }
      },
      resolve,
      reject
    )
  }).catch((error) => {
    console.error('Could not persist Swym share URL', error)
    return null
  })
}

async function shareSwymList(currentList: OmneoList) {
  const listId = currentList.id.toString()

  await swymPromise<void>((swat, resolve, reject) => {
    swat.markListPublic(listId, () => resolve(), reject)
  })

  const shareUrl = await swymPromise<string>((swat, resolve, reject) => {
    swat.generateSharedListURL(listId, (url) => resolve(url), reject)
  })

  if (shareUrl) {
    await persistSwymShareUrl(currentList, shareUrl)
  }

  return getSwymListById(listId)
}

async function deleteSwymList(listId: string | number) {
  return swymPromise<boolean>((swat, resolve, reject) => {
    if (typeof swat.deleteList !== 'function') {
      resolve(false)
      return
    }

    swat.deleteList(listId.toString(), () => resolve(true), reject)
  }).catch((error) => {
    console.error('Could not delete Swym list', error)
    return false
  })
}

async function removeSwymItemFromList(currentList: OmneoList, item: OmneoListItem) {
  const deletePayloads = buildSwymDeletePayloads(currentList, item)
  let lastError: unknown = null

  for (const deletePayload of deletePayloads) {
    const removed = await swymPromise<boolean>((swat, resolve, reject) => {
      swat.deleteFromList(
        currentList.id.toString(),
        deletePayload,
        () => resolve(true),
        reject
      )
    }).catch((error) => {
      lastError = error
      return false
    })

    if (removed) {
      return true
    }
  }

  console.error('Could not remove Swym item from list', {
    currentListId: currentList.id,
    itemId: item.id,
    deletePayloads,
    error: lastError
  })
  return false
}

export const wishlistProviderService = {
  buildListItem(minimumProps: OmneoListItemMeta) {
    return new WishlistListItemDraft({
      sku: minimumProps.variantSku,
      meta: minimumProps
    })
  },

  supportsGuestLists() {
    return true
  },

  isReady() {
    return window.wishlistProvider?.ready === true || Boolean((window as SwymWindow)._swat)
  },

  onReady(callback: () => void) {
    if (this.isReady()) {
      callback()
      return () => {}
    }

    return whenSwymReady(() => {
      callback()
    })
  },

  subscribeToListChanges(callback: () => void) {
    let cleanup = () => {}
    const detachReady = whenSwymReady((swat) => {
      if (!swat.evtLayer) {
        return
      }

      const handleEvent = () => {
        callback()
      }

      SWYM_LIST_EVENT_NAMES.forEach((eventName) => {
        swat.evtLayer?.addEventListener(eventName, handleEvent)
      })

      cleanup = () => {
        SWYM_LIST_EVENT_NAMES.forEach((eventName) => {
          swat.evtLayer?.removeEventListener(eventName, handleEvent)
        })
      }
    })

    return () => {
      detachReady()
      cleanup()
    }
  },

  async isLoggedIn() {
    try {
      const swat = await getSwymRuntime()
      return Boolean(swat.platform?.isLoggedIn?.() ?? hasAuthenticatedCustomerState())
    } catch {
      return hasAuthenticatedCustomerState()
    }
  },

  isCustomerLoggedIn() {
    return hasAuthenticatedCustomerState()
  },

  async getLists(): Promise<OmneoList[]> {
    const lists = await fetchSwymLists()
    setCachedLists(lists)
    return lists
  },

  getCachedLists() {
    return getCachedLists()
  },

  clearCachedLists() {
    clearCachedLists()
  },

  async createList({ name, isShared = false, initialItem = null }: CreateListInput): Promise<CreateListResult> {
    const createdList = await swymPromise<SwymList>((swat, resolve, reject) => {
      swat.createList({ lname: name }, resolve, reject)
    }).catch((error) => {
      console.error('Could not create Swym list', error)
      return {
        errorMessage: extractProviderErrorMessage(error) || 'Could not create new list'
      }
    })

    if (!createdList) {
      return null
    }

    if (isCreateListError(createdList)) {
      return createdList
    }

    if (initialItem) {
      await swymPromise<SwymListItem>((swat, resolve, reject) => {
        swat.addToList(createdList.lid, buildSwymProductInput(initialItem), resolve, reject)
      }).catch((error) => {
        console.error('Could not add initial item to Swym list', error)
        return null
      })
    }

    if (isShared) {
      return shareSwymList(normalizeSwymList(createdList))
    }

    return getSwymListById(createdList.lid)
  },

  async addItemToList(list: OmneoList, item: WishlistListItemDraft) {
    return swymPromise<SwymListItem>((swat, resolve, reject) => {
      swat.addToList(list.id.toString(), buildSwymProductInput(item), resolve, reject)
    }).catch((error) => {
      console.error('Could not add Swym item to list', error)
      return false
    })
  },

  async updateList(currentList: OmneoList, { name, isShared }: UpdateListInput): Promise<OmneoList | null> {
    const nameChanged = name.trim().toLowerCase() !== currentList.name.trim().toLowerCase()
    const shareChanged = isShared !== currentList.is_shared

    if (nameChanged || !shareChanged) {
      return null
    }

    if (currentList.is_shared && !isShared) {
      return null
    }

    if (!currentList.is_shared && isShared) {
      return shareSwymList(currentList)
    }

    return null
  },

  async deleteList(listId: string | number) {
    return deleteSwymList(listId)
  },

  async removeItemFromList(currentList: OmneoList, item: OmneoListItem) {
    return removeSwymItemFromList(currentList, item)
  },

  async getSharedList(handle: string) {
    return getSwymSharedList(handle)
  }
}